/**
 * 代管六個 view 的**路由名分流**（批99-B）。
 *
 * 背景：代管中台的六個頁面（主機清單／主機詳情／任務／任務詳情／告警／開站精靈）
 * 由我方員工與代理商**共用同一份元件**（見 `api/hosting.ts::base()` 的註解——不複製
 * 1857 行的 `HostDetailView`）。但兩個視角的**路由是兩條**：
 *
 * | 視角 | path | name |
 * |---|---|---|
 * | 我方員工 | `/hosts`、`/hosts/:id`… | `hosts`、`host-detail`… |
 * | 代理商 | `/reseller/hosts`、`/reseller/hosts/:id`… | `reseller-hosts`、`reseller-host-detail`… |
 *
 * ★★ 為什麼**不能**共用一條路由（把 `meta.roles` 寫成 `[...STAFF, 'reseller']`）：
 *   `router/index.spec.ts` 釘住「代理商只進得了 `reseller-*` 的頁」這條**結構性**規則
 *   ——它是後端 sub-router 隔離在前端的鏡像。把代理商加進我方路由等於在那道結構閘上
 *   開一個例外，而那正是批93-C 想消滅的東西。vue-router 也不允許同名不同 path
 *   （後者會覆蓋前者），所以只能是兩個 name。
 *
 * 於是共用 view 裡的每個 `RouterLink`／`router.push` 都要問一次「我現在是哪個視角」
 * ——這支 composable 就是那一問，view 端寫 `hr('host-detail')` 即可。
 */
import { useAuthStore } from '@/stores/auth'

/** 代管路由的我方員工版名稱（代理商版＝前綴 `reseller-`）。 */
export const HOSTING_ROUTE_NAMES = [
  'hosts',
  'host-detail',
  'jobs',
  'job-detail',
  'alerts',
  'provision',
] as const
export type HostingRouteName = (typeof HOSTING_ROUTE_NAMES)[number]

/** 純函式版（測試與非元件情境用）。 */
export function hostingRouteName(name: HostingRouteName, isReseller: boolean): string {
  return isReseller ? `reseller-${name}` : name
}

/**
 * 元件內用：`const hr = useHostingRoute()` → `:to="{ name: hr('host-detail'), … }"`。
 *
 * ⚠️ 回傳的是**函式**而不是一份對照表——角色來自 `GET /me`，元件 setup 期可能還沒載回來
 * （`router/index.ts` 的 guard 會先 `await auth.loadMe()`，但直接掛載的測試不會）。
 * 每次呼叫才問 store，模板重新渲染時就會拿到正確的名字。
 */
export function useHostingRoute() {
  const auth = useAuthStore()
  return (name: HostingRouteName): string => hostingRouteName(name, auth.isReseller)
}
